import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Upload, Download, Edit, Plus, X } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import Sidebar from '../components/Sidebar';
import * as api from '../lib/api';

const ProfilePage = () => {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [profile, setProfile] = useState({
    name: user?.name || 'John Doe',
    title: 'Senior Software Engineer',
    location: 'San Francisco, CA',
    hourlyRate: 150,
    bio: 'Experienced software engineer with 8+ years building scalable systems and leading teams.',
    skills: ['Python', 'React', 'AWS', 'Docker', 'Machine Learning'],
    experience: [
      { id: 1, title: 'Senior Software Engineer', company: 'Tech Corp', duration: '2020 - Present', description: 'Leading development of AI-powered features' },
      { id: 2, title: 'Software Engineer', company: 'StartupXYZ', duration: '2017 - 2020', description: 'Built scalable backend systems' }
    ],
    education: [
      { id: 1, degree: 'M.S. Computer Science', school: 'Stanford University', year: '2017' },
      { id: 2, degree: 'B.S. Computer Science', school: 'UC Berkeley', year: '2015' }
    ]
  });
  const [newSkill, setNewSkill] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.getCandidateProfile();
        if (data && data.full_name) {
          setProfile(prev => ({
            ...prev,
            name: data.full_name || prev.name,
            title: data.professional_title || prev.title,
            bio: data.bio || prev.bio,
            skills: data.skills || prev.skills,
            hourlyRate: data.hourly_rate || prev.hourlyRate,
            location: data.location || prev.location,
          }));
        }
      } catch {}
    };
    load();
  }, [user]);

  const handleSave = async () => {
    try {
      await api.updateCandidateProfile({
        full_name: profile.name,
        professional_title: profile.title,
        bio: profile.bio,
        skills: profile.skills,
        hourly_rate: profile.hourlyRate,
        location: profile.location,
      });
    } catch {}
    setEditing(false);
    toast({ title: 'Profile updated!', description: 'Your changes have been saved.' });
  };

  const addSkill = () => {
    if (newSkill.trim() && !profile.skills.includes(newSkill.trim())) {
      setProfile({ ...profile, skills: [...profile.skills, newSkill.trim()] });
      setNewSkill('');
    }
  };

  const removeSkill = (s) => setProfile({ ...profile, skills: profile.skills.filter(sk => sk !== s) });

  return (
    <div className="flex min-h-screen bg-[#F9FBF5]">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-[70px] pt-[60px] md:pt-0 pb-[60px] md:pb-0">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Profile</h1>
            <div className="flex gap-2">
              {editing ? (<><Button onClick={handleSave} className="bg-[#0A3900] hover:bg-[#1A5C0A]">Save Changes</Button><Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button></>) : (<Button onClick={() => setEditing(true)} variant="outline"><Edit className="w-4 h-4 mr-2" />Edit Profile</Button>)}
            </div>
          </div>

          <Tabs defaultValue="overview">
            <TabsList className="mb-6 flex-wrap"><TabsTrigger value="overview">Overview</TabsTrigger><TabsTrigger value="experience">Experience</TabsTrigger><TabsTrigger value="education">Education</TabsTrigger></TabsList>

            <TabsContent value="overview">
              <Card className="p-4 sm:p-6 mb-6">
                <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                  <div className="w-20 h-20 rounded-full bg-[#E0E8D0] flex items-center justify-center text-2xl font-bold text-[#0A3900]">{profile.name.charAt(0)}</div>
                  <div className="flex-1">
                    {editing ? (<><Input value={profile.name} onChange={(e) => setProfile({...profile, name: e.target.value})} className="text-xl font-bold mb-2" /><Input value={profile.title} onChange={(e) => setProfile({...profile, title: e.target.value})} className="mb-2" /><Input value={profile.location} onChange={(e) => setProfile({...profile, location: e.target.value})} /></>) : (<><h2 className="text-2xl font-bold text-gray-900">{profile.name}</h2><p className="text-gray-600">{profile.title}</p><p className="text-gray-500 text-sm">{profile.location}</p></>)}
                  </div>
                  <div className="text-right">
                    {editing ? <Input type="number" value={profile.hourlyRate} onChange={(e) => setProfile({...profile, hourlyRate: parseInt(e.target.value) || 0})} className="w-24" /> : <p className="text-2xl font-bold text-[#0A3900]">${profile.hourlyRate}/hr</p>}
                  </div>
                </div>
              </Card>

              <Card className="p-4 sm:p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">About</h3>
                {editing ? <Textarea value={profile.bio} onChange={(e) => setProfile({...profile, bio: e.target.value})} rows={4} /> : <p className="text-gray-700 leading-relaxed">{profile.bio}</p>}
              </Card>

              <Card className="p-4 sm:p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Skills</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {profile.skills.map((s) => (<Badge key={s} className="text-sm bg-[#E0E8D0] text-[#0A3900]">{s}{editing && <button onClick={() => removeSkill(s)} className="ml-1"><X className="w-3 h-3" /></button>}</Badge>))}
                </div>
                {editing && (<div className="flex gap-2"><Input value={newSkill} onChange={(e) => setNewSkill(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addSkill()} placeholder="Add a skill" /><Button onClick={addSkill}><Plus className="w-4 h-4" /></Button></div>)}
              </Card>
            </TabsContent>

            <TabsContent value="experience">
              <div className="space-y-4">
                {profile.experience.map((e) => (
                  <Card key={e.id} className="p-4 sm:p-6"><h3 className="font-semibold text-gray-900">{e.title}</h3><p className="text-gray-600">{e.company}</p><p className="text-sm text-gray-500">{e.duration}</p><p className="text-gray-700 mt-2">{e.description}</p></Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="education">
              <div className="space-y-4">
                {profile.education.map((e) => (
                  <Card key={e.id} className="p-4 sm:p-6"><h3 className="font-semibold text-gray-900">{e.degree}</h3><p className="text-gray-600">{e.school}</p><p className="text-sm text-gray-500">{e.year}</p></Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
